import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Edit2, Trash2, Archive, RotateCcw, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { useDeleteConfirmState } from '@/components/hooks';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import RockTrackIndicator from '@/components/issues/RockTrackIndicator';
import RockResolutionPanel from '@/components/issues/RockResolutionPanel';
import BalloonTrackIndicator from '@/components/issues/BalloonTrackIndicator';
import BalloonResolutionPanel from '@/components/issues/BalloonResolutionPanel';
import PolicyDialog from '@/components/issues/PolicyDialog';
import CapabilityDialog from '@/components/issues/CapabilityDialog';
import { IssueEnrichButton, IssueEnrichmentDisplay } from '@/components/council/IssueEnrichmentPanel';

export default function IssueTableView({ 
  issues, 
  officers,
  missions = [],
  objectives = [],
  activities = [],
  policies = [],
  capabilities = [],
  onEdit, 
  onDelete,
  onDeleteReportIssue, 
  onArchive, 
  onUnarchive 
}) {
  const [sortConfig, setSortConfig] = useState({ key: 'reported_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [rowsInput, setRowsInput] = useState('50');
  const [gate1Filter, setGate1Filter] = useState('all');
  const [gate2Filter, setGate2Filter] = useState('all');
  const deleteConfirm = useDeleteConfirmState({ confirmTimeout: 3000 });
  const navigate = useNavigate();
  const [rockPanelIssue, setRockPanelIssue] = useState(null);
  const [balloonPanelIssue, setBalloonPanelIssue] = useState(null);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [showCapabilityDialog, setShowCapabilityDialog] = useState(false);
  const [policyIssueId, setPolicyIssueId] = useState(null);
  const [capabilityIssueId, setCapabilityIssueId] = useState(null);
  const [enrichmentIssue, setEnrichmentIssue] = useState(null);

  const sourceTextColors = {
    mission: 'text-amber-600',
    objective: 'text-indigo-500',
    activity: 'text-red-500',
    task: 'text-purple-500',
    progress_report: 'text-orange-500',
    scan: 'text-emerald-500',
    manual: 'text-blue-500'
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredByGates = issues.filter(issue => {
    if (gate1Filter !== 'all' && (issue.classification_gate1 || 'pending') !== gate1Filter) return false;
    if (gate2Filter !== 'all' && (issue.classification_gate2 || 'pending') !== gate2Filter) return false;
    return true;
  });

  const SEVERITY_ORDER = { critical: 4, high: 3, medium: 2, low: 1 };

  const sortedIssues = [...filteredByGates].sort((a, b) => {
    const { key, direction } = sortConfig;
    let aVal = a[key];
    let bVal = b[key];

    // Handle nulls/undefined — push them to the end regardless of direction
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    // Handle severity with defined order
    if (key === 'severity') {
      aVal = SEVERITY_ORDER[aVal] || 0;
      bVal = SEVERITY_ORDER[bVal] || 0;
    }
    // Handle dates
    else if (key === 'reported_at' || key === 'created_date') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }
    // Handle strings
    else if (typeof aVal === 'string' && typeof bVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  const renderSortHeader = (label, sortKey) => (
    <TableHead 
      className="cursor-pointer hover:bg-slate-200 transition-colors text-slate-700"
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.key === sortKey ? (
          sortConfig.direction === 'asc' ? 
            <ArrowUp className="w-3 h-3" /> : 
            <ArrowDown className="w-3 h-3" />
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-50" />
        )}
      </div>
    </TableHead>
  );

  const getReporter = (issue) => {
    const reporter = officers.find(o => o.user_email === issue.reported_by);
    return reporter ? reporter.callsign : issue.reported_by;
  };

  const getSourceContext = (issue) => {
    let activity = activities?.find(a => a.id === issue.linked_activity_id);
    let objective = objectives.find(o => o.id === issue.linked_objective_id);
    let mission = missions?.find(m => m.id === issue.linked_mission_id);

    if (!objective && activity) {
      objective = objectives.find(o => o.id === activity.objective_id);
    }
    if (!mission && objective) {
      mission = missions?.find(m => m.id === objective.mission_id);
    }

    if (issue.source !== 'manual' && !activity && !objective && !mission) {
      if (issue.source === 'activity' || issue.source === 'progress_report' || issue.source === 'task') {
        activity = activities?.find(a => a.id === issue.source_id || a.id === issue.activity_id);
        if (activity) {
          objective = objectives.find(o => o.id === activity.objective_id);
          if (objective) {
            mission = missions?.find(m => m.id === objective.mission_id);
          }
        }
      } else if (issue.source === 'objective') {
        objective = objectives.find(o => o.id === issue.source_id);
        if (objective) {
          mission = missions?.find(m => m.id === objective.mission_id);
        }
      } else if (issue.source === 'mission') {
        mission = missions?.find(m => m.id === issue.source_id);
      }
    }

    const parts = [];
    if (mission) parts.push({ code: mission.mission_code, name: mission.title });
    if (objective) parts.push({ code: objective.objective_code, name: `${objective.statement?.substring(0, 30)}...` });
    if (activity) parts.push({ code: activity.activity_code, name: activity.title });
    return parts.length > 0 ? parts : null;
  };

  const formatCategory = (cat) => cat?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-';
  const formatStatus = (status) => status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-';
  const formatSeverity = (sev) => sev ? sev.charAt(0).toUpperCase() + sev.slice(1) : '-';
  const formatSource = (issue) => {
    const src = issue.source || issue.displayType?.toLowerCase() || 'manual';
    if (src === 'progress_report') return 'Progress Report';
    return src.charAt(0).toUpperCase() + src.slice(1);
  };
  const getSourceColor = (issue) => {
    const src = issue.source || issue.displayType?.toLowerCase() || 'manual';
    return sourceTextColors[src] || 'text-slate-700';
  };

  const getFilteredTags = (issue, tagType) => {
    const tags = issue[tagType] || [];
    const removedTags = issue.removed_tags || [];
    return tags.filter(tag => !removedTags.some(rt => rt.tag_type === tagType && rt.tag_value === tag));
  };

  const renderTagBadges = (tags, colorClass) => {
    if (!tags || tags.length === 0) return <span className="text-slate-400">-</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {tags.map((tag, idx) => (
          <Badge key={idx} variant="outline" className={`text-xs ${colorClass}`}>
            {typeof tag === 'string' ? (tag.includes('@') ? officers.find(o => o.user_email === tag)?.callsign || tag : tag) : tag}
          </Badge>
        ))}
      </div>
    );
  };

  // Pagination
  const totalRows = sortedIssues.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
  const paginatedIssues = sortedIssues.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [issues.length, rowsPerPage]);

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    setRowsInput(value);
    const num = parseInt(value, 10);
    if (num > 0) {
      setRowsPerPage(num);
    }
  };

  const getResolutionOwner = (issue) => {
    if (!issue.resolution_owner) return '-';
    const officer = officers.find(o => o.user_email === issue.resolution_owner);
    return officer ? officer.callsign : issue.resolution_owner;
  };

  const frequencyLabels = { first_occurrence: 'First', recurring: 'Recurring', escalating: 'Escalating' };
  const frequencyColors = { first_occurrence: 'text-slate-500', recurring: 'text-amber-600', escalating: 'text-red-600' };
  const gate1Labels = { pending: 'Pending', noise: 'Noise', signal: 'Signal' };
  const gate1Colors = { pending: 'bg-slate-100 text-slate-600', noise: 'bg-gray-100 text-gray-600', signal: 'bg-amber-100 text-amber-700' };
  const gate2Labels = { pending: 'Pending', balloon: '🎈 Balloon', rock: '🪨 Rock' };
  const gate2Colors = { pending: 'bg-slate-100 text-slate-600', balloon: 'bg-sky-100 text-sky-700', rock: 'bg-stone-200 text-stone-700' };

  return (
    <div className="space-y-3">
      {/* Gate Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span>Gate 1:</span>
          <select
            value={gate1Filter}
            onChange={(e) => { setGate1Filter(e.target.value); setCurrentPage(1); }}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded px-2 py-1"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="noise">Noise</option>
            <option value="signal">Signal</option>
          </select>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span>Gate 2:</span>
          <select
            value={gate2Filter}
            onChange={(e) => { setGate2Filter(e.target.value); setCurrentPage(1); }}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded px-2 py-1"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="balloon">Balloon</option>
            <option value="rock">Rock</option>
          </select>
        </div>
      </div>

      {/* Pagination Header */}
      <div className="flex items-center justify-between text-sm text-slate-400">
        <div className="flex items-center gap-2">
          <span>Total: <span className="font-semibold text-slate-200">{totalRows}</span> rows</span>
          <span className="text-slate-600">|</span>
          <span>Showing {startIndex + 1}-{endIndex} of {totalRows}</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <Input data-testid="issues-issuetableview-input"
              type="number"
              min="1"
              value={rowsInput}
              onChange={handleRowsPerPageChange}
              className="w-20 h-8 bg-slate-800 border-slate-700 text-slate-100 text-center"
            />
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="h-8 px-2 border-slate-700 text-slate-300"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-2">Page {currentPage} of {totalPages || 1}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="h-8 px-2 border-slate-700 text-slate-300"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-md border border-slate-300 overflow-hidden bg-white">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-100 hover:bg-slate-100">
            <TableHead className="w-12 text-slate-700">#</TableHead>
            {renderSortHeader("Title", "title")}
            {renderSortHeader("Context", "source")}
            {renderSortHeader("Category", "category")}
            {renderSortHeader("Severity", "severity")}
            {renderSortHeader("Status", "status")}
            {renderSortHeader("Source", "source")}
            {renderSortHeader("Reported By", "reported_by")}
            {renderSortHeader("Date", "reported_at")}
            {renderSortHeader("Freq.", "frequency_signal")}
            {renderSortHeader("Gate 1", "classification_gate1")}
            {renderSortHeader("Gate 2", "classification_gate2")}
            <TableHead className="text-slate-700">Tracks</TableHead>
            <TableHead className="text-slate-700">Decision</TableHead>
            <TableHead className="text-slate-700">Res. Owner</TableHead>
            <TableHead className="text-slate-700">Officers</TableHead>
            <TableHead className="text-slate-700">Artifacts</TableHead>
            <TableHead className="text-slate-700">Rituals</TableHead>
            <TableHead className="text-slate-700">Policies</TableHead>
            <TableHead className="text-slate-700">Capabilities</TableHead>
            <TableHead className="text-right text-slate-700">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedIssues.map((issue, index) => {
            const sourceContext = getSourceContext(issue);
            const rowNumber = startIndex + index + 1;
            return (
            <TableRow key={`${issue.source}-${issue.id}`} className="hover:bg-slate-50 border-slate-200">
              <TableCell className="text-slate-500 text-sm font-mono">{rowNumber}</TableCell>
              <TableCell className="font-medium max-w-xs text-slate-900">
                <div className="truncate" title={issue.title}>{issue.title}</div>
                <div className="text-xs text-slate-500 font-mono truncate">{issue.id}</div>
              </TableCell>
              <TableCell className="text-sm max-w-xs">
                {sourceContext ? (
                  <div className="text-xs text-slate-600">
                    {sourceContext.map((item, idx) => (
                      <span key={idx}>
                        {idx > 0 && ' → '}
                        <span 
                          className="font-bold text-blue-600 hover:text-blue-500 cursor-pointer underline"
                          onClick={() => navigate(createPageUrl('civilizationmap') + `?search=${encodeURIComponent(item.code)}`)}
                        >
                          [{item.code}]
                        </span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-slate-400">-</span>
                )}
              </TableCell>
              <TableCell className="text-slate-700 text-sm">
                {formatCategory(issue.category)}
              </TableCell>
              <TableCell className="text-slate-700 text-sm">
                {formatSeverity(issue.severity)}
              </TableCell>
              <TableCell className="text-slate-700 text-sm">
                {formatStatus(issue.status)}
              </TableCell>
              <TableCell className={`text-sm font-medium ${getSourceColor(issue)}`}>
                {formatSource(issue)}
              </TableCell>
              <TableCell className="text-slate-700 text-sm">
                {getReporter(issue)}
              </TableCell>
              <TableCell className="text-slate-700 text-sm">
                {issue.reported_at ? format(new Date(issue.reported_at), 'MMM d, yyyy') :
                 issue.created_date ? format(new Date(issue.created_date), 'MMM d, yyyy') : '-'}
              </TableCell>
              <TableCell className={`text-sm font-medium ${frequencyColors[issue.frequency_signal] || 'text-slate-500'}`}>
                {frequencyLabels[issue.frequency_signal] || '-'}
              </TableCell>
              <TableCell className="text-sm">
                {issue.classification_gate1 ? (
                  <Badge className={`text-xs ${gate1Colors[issue.classification_gate1] || gate1Colors.pending}`}>
                    {gate1Labels[issue.classification_gate1] || 'Pending'}
                  </Badge>
                ) : '-'}
              </TableCell>
              <TableCell className="text-sm">
                {issue.classification_gate1 === 'signal' && issue.classification_gate2 ? (
                  <Badge className={`text-xs ${gate2Colors[issue.classification_gate2] || gate2Colors.pending}`}>
                    {gate2Labels[issue.classification_gate2] || 'Pending'}
                  </Badge>
                ) : '-'}
              </TableCell>
              <TableCell className="text-sm">
                {issue.classification_gate2 === 'rock' && (
                  <RockTrackIndicator issue={issue} onClick={() => setRockPanelIssue(issue)} />
                )}
                {issue.classification_gate2 === 'balloon' && (
                  <BalloonTrackIndicator issue={issue} onClick={() => setBalloonPanelIssue(issue)} />
                )}
                {issue.classification_gate2 !== 'rock' && issue.classification_gate2 !== 'balloon' && (
                  <span className="text-slate-400">-</span>
                )}
              </TableCell>
              <TableCell className="text-sm">
                {issue.classification_gate2 === 'rock' ? (
                  <div className="space-y-1">
                    {issue.rock_track2_pathway && (
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs block w-fit">
                        T2: {issue.rock_track2_pathway.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </Badge>
                    )}
                    {issue.decision && (
                      <Badge className="bg-amber-100 text-amber-700 text-xs block w-fit">
                        T3: {issue.decision.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </Badge>
                    )}
                    {!issue.rock_track2_pathway && !issue.decision && <span className="text-slate-400">-</span>}
                  </div>
                ) : issue.classification_gate2 === 'balloon' && issue.decision ? (
                  <Badge className="bg-sky-100 text-sky-700 text-xs">
                    {issue.decision.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </Badge>
                ) : issue.decision ? (
                  <span className="text-slate-700 text-xs">{issue.decision.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                ) : (
                  <span className="text-slate-400">-</span>
                )}
              </TableCell>
              <TableCell className="text-sm text-slate-700">
                {getResolutionOwner(issue)}
              </TableCell>
              <TableCell className="text-sm">
                {renderTagBadges(getFilteredTags(issue, 'tagged_officers'), 'border-indigo-500/30 text-indigo-600')}
              </TableCell>
              <TableCell className="text-sm">
                {renderTagBadges(getFilteredTags(issue, 'tagged_artifacts'), 'border-cyan-500/30 text-cyan-600')}
              </TableCell>
              <TableCell className="text-sm">
                {renderTagBadges(getFilteredTags(issue, 'tagged_rituals'), 'border-purple-500/30 text-purple-600')}
              </TableCell>
              <TableCell className="text-sm">
                {renderTagBadges(getFilteredTags(issue, 'tagged_policies'), 'border-amber-500/30 text-amber-600')}
              </TableCell>
              <TableCell className="text-sm">
                {renderTagBadges(getFilteredTags(issue, 'tagged_capabilities'), 'border-emerald-500/30 text-emerald-600')}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  {!issue.progress_report_id && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-purple-500 hover:text-purple-400"
                      onClick={() => setEnrichmentIssue(issue)}
                      title="AI Enrichment"
                    >
                      <Sparkles className="w-4 h-4" />
                    </Button>
                  )}
                  {onEdit && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-600 hover:text-slate-900" onClick={() => onEdit(issue)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  )}
                  {!issue.progress_report_id && (
                    (issue.status === 'resolved' || issue.status === 'archived') && onUnarchive ? (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-slate-600 hover:text-slate-900"
                        onClick={() => onUnarchive.mutate(issue.id)}
                        title="Unarchive"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    ) : onArchive && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-slate-600 hover:text-slate-900"
                        onClick={() => onArchive.mutate(issue.id)}
                        title="Archive"
                      >
                        <Archive className="w-4 h-4" />
                      </Button>
                    )
                  )}
                  {(onDelete || onDeleteReportIssue) && (
                    <div className="relative">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-slate-600 hover:text-red-500"
                        onClick={() => {
                          const deleteMutation = issue.progress_report_id ? onDeleteReportIssue : onDelete;
                          if (deleteMutation) {
                            deleteConfirm.checkAndConfirm(issue.id, () => deleteMutation.mutate(issue.id));
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      {deleteConfirm.isPendingConfirm(issue.id) && (
                        <span className="absolute right-0 -bottom-8 text-xs text-red-500 bg-white px-2 py-1 rounded border border-red-300 whitespace-nowrap z-10">
                          Click again to confirm
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </TableCell>
            </TableRow>
          )})}
        </TableBody>
      </Table>
      {paginatedIssues.length === 0 && (
        <div className="text-center py-8 text-slate-500">No issues to display</div>
      )}
    </div>

      {/* Rock Resolution Dialog */}
      <Dialog open={!!rockPanelIssue} onOpenChange={(open) => !open && setRockPanelIssue(null)}>
        <DialogContent data-testid="issues-issuetableview-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Rock Resolution: {rockPanelIssue?.title}</DialogTitle>
          </DialogHeader>
          {rockPanelIssue && (
            <RockResolutionPanel
              issue={rockPanelIssue}
              officers={officers}
              policies={policies}
              capabilities={capabilities}
              onCreatePolicy={(issueId) => { setPolicyIssueId(issueId); setShowPolicyDialog(true); }}
              onCreateCapability={(issueId) => { setCapabilityIssueId(issueId); setShowCapabilityDialog(true); }}
              onComplete={() => setRockPanelIssue(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Balloon Resolution Dialog */}
      <Dialog open={!!balloonPanelIssue} onOpenChange={(open) => !open && setBalloonPanelIssue(null)}>
        <DialogContent data-testid="issues-issuetableview-dialog-1" className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Balloon Resolution: {balloonPanelIssue?.title}</DialogTitle>
          </DialogHeader>
          {balloonPanelIssue && (
            <BalloonResolutionPanel
              issue={balloonPanelIssue}
              officers={officers}
              onComplete={() => setBalloonPanelIssue(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <PolicyDialog
        open={showPolicyDialog}
        onClose={() => setShowPolicyDialog(false)}
        policy={{ linked_issue_ids: policyIssueId ? [policyIssueId] : [], interim_for_capability_development: true }}
        officers={officers}
        issues={issues}
        capabilities={capabilities}
      />

      <CapabilityDialog
        open={showCapabilityDialog}
        onClose={() => setShowCapabilityDialog(false)}
        capability={{ linked_issue_ids: capabilityIssueId ? [capabilityIssueId] : [] }}
        officers={officers}
        issues={issues}
        policies={policies}
        objectives={objectives}
      />

      {/* AI Enrichment Dialog */}
      <Dialog open={!!enrichmentIssue} onOpenChange={(open) => !open && setEnrichmentIssue(null)}>
        <DialogContent data-testid="issues-issuetableview-dialog-2" className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-400" />
              AI Enrichment: {enrichmentIssue?.title}
            </DialogTitle>
          </DialogHeader>
          {enrichmentIssue?.ai_enrichment ? (
            <IssueEnrichmentDisplay enrichment={enrichmentIssue.ai_enrichment} />
          ) : enrichmentIssue ? (
            <div className="flex flex-col items-center gap-4 py-6">
              <p className="text-sm text-slate-400 text-center">This issue hasn't been AI-enriched yet.</p>
              <IssueEnrichButton
                issue={enrichmentIssue}
                onEnriched={(id, enrichment) => {
                  setEnrichmentIssue({ ...enrichmentIssue, ai_enrichment: enrichment });
                }}
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}